import { useId, type ReactNode, type SelectHTMLAttributes } from 'react';

import { cx } from '../../lib/class-names';

import styles from './FormField.module.css';

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  hint?: ReactNode;
  error?: string;
}

export function SelectField({
  label,
  hint,
  error,
  className,
  required,
  children,
  ...props
}: SelectFieldProps): React.JSX.Element {
  const id = useId();
  const describedBy =
    [hint === undefined ? null : `${id}-hint`, error === undefined ? null : `${id}-error`]
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <div className={cx(styles.field, className)}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && <span className={styles.required}> *</span>}
      </label>
      <select
        {...props}
        id={id}
        required={required}
        aria-label={label}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy}
        className={cx(styles.input, styles.select, error !== undefined && styles.inputInvalid)}
      >
        {children}
      </select>
      {hint !== undefined && (
        <p className={styles.hint} id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p className={styles.error} id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
